import * as React from "react";
import PropTypes from "prop-types";
import Backbone from "backbone";
import UserAgent from "./UserAgent";

const isIOS = UserAgent.isIOS();
// In iOS, elements with style '-webkit-overflow-scrolling: touch' have 2 major
// bugs:
// 1) When the browser switches from portrait to landscape mode: If there was no
// vertical overflow before, but afterwards there is, vertical scrolling
// remains disabled. Toggle this property and the 'overflow-y: auto' so that the
// webview recalculates vertical scrolling. NOTE: This bug seems to have stopped
// mattering since we implemented the orientationchange fix. Perhaps our use of
// flexbox fixed the use cases? Leaving the fix since I don't know why it's not
// needed.
// 2) If an input is focused so that a cursor appears, the cursor will not follow
// the input while the container is scrolling; it will only snap into place after
// the container stops moving. Toggle the property when a child input is focused.

// Also worth noting, iOS doesn't handle absolute positioning within a scrollable
// container well. DO NOT put absolutely positioned content inside a div like this

// In order to prevent scrolling while dragging something (like a slider or
// slide-to-unlock control), we change overflow-y on all ScrollableDivs when we
// receive events on this global scroll killswitch listener.
const scrollKillSwitchListener = { ...Backbone.Events };
let isScrollDead = false;
scrollKillSwitchListener.on("kill", () => {
  isScrollDead = true;
});
scrollKillSwitchListener.on("revive", () => {
  isScrollDead = false;
});

export function killScroll() {
  scrollKillSwitchListener.trigger("kill");
}

export function reviveScroll() {
  scrollKillSwitchListener.trigger("revive");
}

export default class ScrollableDiv extends React.Component {
  static propTypes = {
    style: PropTypes.object,
    children: PropTypes.node,
    captureRef: PropTypes.func,
    elementType: PropTypes.string,
  };

  static defaultProps = {
    elementType: "div",
  };

  componentDidMount() {
    scrollKillSwitchListener.on(
      "kill revive",
      () => this._updateContainerStyle(),
      this
    );

    if (!isIOS) {
      return;
    }
    window.addEventListener("orientationchange", this._handleOrientationChange);
    this._container.addEventListener("focusin", this._handleFocusIn);
    this._container.addEventListener("focusout", this._handleFocusOut);
  }

  componentWillUnmount() {
    scrollKillSwitchListener.off(null, null, this);

    if (!isIOS) {
      return;
    }
    window.removeEventListener(
      "orientationchange",
      this._handleOrientationChange
    );
    this._container.removeEventListener("focusin", this._handleFocusIn);
    this._container.removeEventListener("focusout", this._handleFocusOut);
  }

  _handleOrientationChange = () => {
    clearTimeout(this._timeout1);
    clearTimeout(this._timeout2);

    this._timeout1 = setTimeout(() => {
      this._justChangedOrientation = true;
      this._updateContainerStyle();

      this._timeout2 = setTimeout(() => {
        this._justChangedOrientation = false;
        this._updateContainerStyle();
      }, 100);
    }, 100);
  };

  _handleFocusIn = (event) => {
    const target = event.target;
    if (target && target.nodeName === "INPUT") {
      this._keyboardShown = true;
      this._updateContainerStyle();
    }
  };

  _handleFocusOut = (event) => {
    const target = event.target;
    if (target && target.nodeName === "INPUT") {
      this._keyboardShown = false;
      this._updateContainerStyle();
    }
  };

  _computedContainerStyles() {
    if (isScrollDead) {
      return {
        WebkitOverflowScrolling: "auto",
        overflowY: "hidden",
      };
    } else if (!this._justChangedOrientation) {
      return {
        WebkitOverflowScrolling: this._keyboardShown ? "auto" : "touch",
        overflowY: "auto",
      };
    } else {
      return {
        WebkitOverflowScrolling: this._keyboardShown ? "touch" : "auto",
        overflowY: "hidden",
      };
    }
  }

  _updateContainerStyle = () => {
    if (!this._container) {
      return;
    }

    Object.assign(this._container.style, this._computedContainerStyles());
  };

  _captureRef = (el) => {
    this._container = el;
    if (this.props.captureRef) {
      this.props.captureRef(el);
    }
  };

  render() {
    const {
      children,
      style,
      captureRef, // eslint-disable-line no-unused-vars
      elementType,
      ...attributes
    } = this.props;

    const ElementType = elementType;

    return (
      <ElementType
        ref={this._captureRef}
        style={{
          overflowX: "hidden",
          ...style,
          ...this._computedContainerStyles(),
        }}
        {...attributes}
      >
        {children}
      </ElementType>
    );
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/ScrollableDiv.jsx